/**
 * @file orderQuery.service.ts
 * @description Dedicated service for fetching and hydrating active kitchen orders.
 * 
 * ARCHITECTURAL RESPONSIBILITIES:
 * 1. Queries orders joined with order_items and item catalog names.
 * 2. Normalizes database rows into touch-optimized KitchenOrder domain entities.
 * 3. Provides real-time channel setup for instantaneous kitchen display updates.
 */

import { supabase } from '../../../config/supabase.config';
import type { KitchenOrder, KitchenOrderItem, KitchenOrderItemModifier, KitchenOrderStatus, KitchenPriority } from '../../../types/order.types';

// Map database statuses to chef KDS statuses
const normalizeStatus = (status: string | null): KitchenOrderStatus => {
  if (status === 'pending') return 'new';
  if (status === 'preparing') return 'preparing';
  if (status === 'ready') return 'ready';
  if (status === 'completed') return 'done';
  if (status === 'cancelled') return 'cancelled';
  return 'new';
};

/**
 * Fetches all currently active orders for a venue that need kitchen attention.
 */
export const fetchActiveKitchenOrders = async (venueId: string): Promise<KitchenOrder[]> => {
  // Restricts active orders to the current 24-hour shift to prevent stale orders from lingering on the kitchen display
  const cutoffIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('orders')
    .select(`
      id,
      venue_id,
      table_id,
      status,
      priority,
      total_amount,
      type,
      waiter_id,
      payment_method,
      payment_status,
      created_at,
      confirmed_to_kitchen_at,
      served_at,
      metadata,
      order_items (
        id,
        order_id,
        item_id,
        price,
        quantity,
        notes,
        status,
        kitchen_station,
        course,
        cancelled_reason,
        created_at,
        selected_modifiers,
        items (
          name,
          kitchen_station
        )
      )
    `)
    .eq('venue_id', venueId)
    .in('status', ['pending', 'confirmed', 'preparing', 'ready', 'completed'])
    .gte('created_at', cutoffIso)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[orderQuery.service] Failed to fetch active orders:', error);
    throw error;
  }

  // Excludes unconfirmed online checkout sessions so chefs only prepare paid online orders
  const confirmedData = (data || []).filter(
    (row: any) => !(row.payment_method === 'ONLINE' && row.payment_status !== 'paid')
  );

  return confirmedData.map((row: any) => {
    const meta = row.metadata || {};
    const rawTable = meta.table_number || row.table_id;
    const tableNum = rawTable
      ? String(rawTable).toLowerCase().startsWith('table') || String(rawTable).toLowerCase().startsWith('t-')
        ? rawTable
        : `Table ${rawTable}`
      : 'Takeaway';
    const orderNumber = row.id ? `#${row.id.slice(-4).toUpperCase()}` : '#0000';

    const items: KitchenOrderItem[] = (row.order_items || []).map((oi: any) => {
      let rawMods = oi.selected_modifiers;
      if (typeof rawMods === 'string' && rawMods.trim()) {
        try {
          rawMods = JSON.parse(rawMods);
        } catch {
          rawMods = [];
        }
      }
      if (!Array.isArray(rawMods) || rawMods.length === 0) {
        rawMods = meta.modifiers || [];
        if (typeof rawMods === 'string' && rawMods.trim()) {
          try {
            rawMods = JSON.parse(rawMods);
          } catch {
            rawMods = [];
          }
        }
      }

      const normalizedModifiers: KitchenOrderItemModifier[] = Array.isArray(rawMods)
        ? rawMods.map((m: any) => {
            if (typeof m === 'string') {
              return { name: m };
            }
            return {
              id: m.id,
              name: m.name || m.title || 'Modifier',
              price: m.price,
              group_name: m.group_name || m.groupName,
              group_type: m.group_type || m.groupType,
              quantity: m.quantity,
              is_free: m.is_free,
              total_price: m.total_price,
            };
          })
        : [];

      return {
        id: oi.id,
        order_id: oi.order_id,
        item_id: oi.item_id,
        name: oi.items?.name || 'Menu Item',
        quantity: oi.quantity,
        price: oi.price,
        notes: oi.notes,
        cooking_style: meta.cooking_style || null,
        modifiers: normalizedModifiers,
        kitchen_station: oi.kitchen_station || oi.items?.kitchen_station || 'hot',
        course: oi.course || 'main',
        status: oi.status || 'pending',
        cancelled_reason: oi.cancelled_reason || null,
        created_at: oi.created_at,
      };
    });

    const isConfirmed = Boolean(
      row.status === 'confirmed' ||
      row.confirmed_to_kitchen_at ||
      meta.confirmed_to_kitchen
    );

    return {
      id: row.id,
      venue_id: row.venue_id,
      table_id: row.table_id,
      table_number: String(tableNum),
      order_number: orderNumber,
      status: normalizeStatus(row.status),
      raw_db_status: row.status || 'pending',
      is_confirmed_to_kitchen: isConfirmed,
      confirmed_to_kitchen_at: row.confirmed_to_kitchen_at || meta.confirmed_to_kitchen_at || null,
      priority: (row.priority as KitchenPriority) || 'normal',
      total_amount: Number(row.total_amount) || 0,
      type: row.type || 'dine-in',
      waiter_id: row.waiter_id,
      waiter_name: meta.waiter_name || 'Staff',
      guest_count: meta.guest_count || 1,
      customer_notes: meta.notes || null,
      allergens: meta.allergens || [],
      created_at: row.created_at,
      ready_at: (row as any).ready_at || meta.ready_at || null,
      served_at: row.served_at,
      items,
    };
  });
};
